'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bold, Italic, Underline, AlignLeft, AlignCenter,
    AlignRight, AlignJustify, List, ListOrdered, Heading2, Heading3,
    Link2, Unlink, ChevronUp, ChevronDown, Trash2, Plus, X,
} from 'lucide-react';
import { HeroSlide, api } from '@/lib/api';

// ─── Rich Editor helpers (same pattern as about/page.tsx) ───────────────────

function exec(command: string, value?: string) {
    document.execCommand(command, false, value);
}

function saveSelection() {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
}

function restoreSelection(range: Range | null) {
    if (!range) return;
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
}

function ToolbarBtn({ command, value, title, children }: {
    command: string; value?: string; title: string; children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); exec(command, value); }}
            title={title}
            className="p-1 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
        >
            {children}
        </button>
    );
}

function RichToolbar() {
    const [fontSize, setFontSize] = useState(3);
    const savedRange = useRef<Range | null>(null);

    const changeFontSize = (delta: number) => {
        const next = Math.min(7, Math.max(1, fontSize + delta));
        setFontSize(next);
        restoreSelection(savedRange.current);
        exec('styleWithCSS', 'true');
        exec('fontSize', String(next));
    };

    return (
        <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-slate-300 bg-slate-100 sticky top-0 z-10">
            <ToolbarBtn command="formatBlock" value="h2" title="Heading 2"><Heading2 className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="formatBlock" value="h3" title="Heading 3"><Heading3 className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="formatBlock" value="p" title="Paragraph">
                <span className="text-xs font-medium px-0.5">¶</span>
            </ToolbarBtn>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); changeFontSize(-1); }}
                disabled={fontSize <= 1}
                className="p-1 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer disabled:opacity-30 text-xs font-bold"
            >A-</button>
            <span className="text-xs text-slate-400 w-3 text-center">{fontSize}</span>
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); changeFontSize(1); }}
                disabled={fontSize >= 7}
                className="p-1 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer disabled:opacity-30 text-xs font-bold"
            >A+</button>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <ToolbarBtn command="bold" title="Bold"><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="italic" title="Italic"><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="underline" title="Underline"><Underline className="w-3.5 h-3.5" /></ToolbarBtn>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <ToolbarBtn command="justifyLeft" title="Align Left"><AlignLeft className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="justifyCenter" title="Align Center"><AlignCenter className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="justifyRight" title="Align Right"><AlignRight className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="justifyFull" title="Justify"><AlignJustify className="w-3.5 h-3.5" /></ToolbarBtn>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <ToolbarBtn command="insertUnorderedList" title="Bullet List"><List className="w-3.5 h-3.5" /></ToolbarBtn>
            <ToolbarBtn command="insertOrderedList" title="Ordered List"><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <button
                type="button"
                onMouseDown={(e) => {
                    e.preventDefault();
                    const range = saveSelection();
                    const url = window.prompt('URL');
                    if (url) { restoreSelection(range); exec('createLink', url.startsWith('http') ? url : `https://${url}`); }
                }}
                className="p-1 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
            ><Link2 className="w-3.5 h-3.5" /></button>
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); exec('unlink'); }}
                className="p-1 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
            ><Unlink className="w-3.5 h-3.5" /></button>
            <div className="w-px h-4 bg-slate-300 mx-0.5" />
            <label className="cursor-pointer flex items-center gap-1 text-slate-600 hover:bg-slate-200 p-1 transition-colors">
                <span className="text-xs font-medium">A</span>
                <input
                    type="color"
                    defaultValue="#ffffff"
                    className="w-4 h-4 rounded cursor-pointer border-0 p-0 bg-transparent"
                    onMouseDown={() => { savedRange.current = saveSelection(); }}
                    onChange={(e) => { restoreSelection(savedRange.current); exec('foreColor', e.target.value); }}
                />
            </label>
            <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}
                className="text-xs text-slate-400 hover:text-slate-700 px-1 cursor-pointer"
            >✕</button>
        </div>
    );
}

function RichEditor({ label, initialContent, onBlur }: {
    label: string; initialContent: string; onBlur: (html: string) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [sourceMode, setSourceMode] = useState(false);
    const [sourceHtml, setSourceHtml] = useState('');
    const pendingHtml = useRef<string | null>(null);
    const initialized = useRef(false);

    useEffect(() => {
        if (ref.current && initialContent && !initialized.current) {
            ref.current.innerHTML = initialContent;
            initialized.current = true;
        }
    }, [initialContent]);

    useEffect(() => {
        if (!sourceMode && pendingHtml.current !== null && ref.current) {
            ref.current.innerHTML = pendingHtml.current;
            pendingHtml.current = null;
        }
    }, [sourceMode]);

    useEffect(() => {
        if (sourceMode) onBlur(sourceHtml);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceHtml]);

    const enterSource = () => {
        const html = ref.current ? ref.current.innerHTML : initialContent;
        setSourceHtml(html);
        setSourceMode(true);
    };

    const exitSource = () => {
        pendingHtml.current = sourceHtml;
        onBlur(sourceHtml);
        setSourceMode(false);
    };

    return (
        <div className="flex flex-col flex-1 min-w-0">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</span>
            <div className="border border-slate-300 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between border-b border-slate-300 bg-slate-100">
                    {sourceMode ? (
                        <div className="flex items-center gap-2 px-2 py-1">
                            <span className="text-xs text-slate-400 font-mono">HTML source</span>
                        </div>
                    ) : (
                        <RichToolbar />
                    )}
                    <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); sourceMode ? exitSource() : enterSource(); }}
                        className={`px-2 py-1 text-xs font-mono font-medium transition-colors cursor-pointer shrink-0 ${sourceMode ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-200'}`}
                    >{'</>'}</button>
                </div>
                {sourceMode ? (
                    <textarea
                        value={sourceHtml}
                        onChange={(e) => setSourceHtml(e.target.value)}
                        className="flex-1 p-3 font-mono text-xs text-slate-700 outline-none resize-none min-h-[120px] bg-white"
                        spellCheck={false}
                    />
                ) : (
                    <div
                        ref={ref}
                        contentEditable
                        suppressContentEditableWarning
                        onFocus={() => exec('defaultParagraphSeparator', 'p')}
                        onInput={() => ref.current && onBlur(ref.current.innerHTML)}
                        onBlur={() => ref.current && onBlur(ref.current.innerHTML)}
                        className="flex-1 p-3 about-content outline-none min-h-[120px] text-slate-800 bg-white"
                    />
                )}
            </div>
        </div>
    );
}

// ─── Slide Row ───────────────────────────────────────────────────────────────

interface SlideRowProps {
    slide: HeroSlide;
    index: number;
    total: number;
    isExpanded: boolean;
    onToggle: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onSave: (updates: Partial<HeroSlide>) => Promise<void>;
    onDelete: () => Promise<void>;
}

function SlideRow({ slide, index, total, isExpanded, onToggle, onMoveUp, onMoveDown, onSave, onDelete }: SlideRowProps) {
    const t = useTranslations('landing.heroSlideshow');

    // Local editable state
    const [textEn, setTextEn] = useState(slide.text_en ?? '');
    const [textPt, setTextPt] = useState(slide.text_pt ?? '');
    const [position, setPosition] = useState<'left' | 'right'>(slide.text_position as 'left' | 'right');
    const [duration, setDuration] = useState(slide.duration_seconds);
    const [imagePath, setImagePath] = useState(slide.image_path ?? '');
    const [activeTab, setActiveTab] = useState<'en' | 'pt'>('en');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const result = await api.uploadSlideImage(file);
            setImagePath(result.image_url);
        } catch (err) {
            console.error('Image upload failed', err);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({
                text_en: textEn,
                text_pt: textPt,
                text_position: position,
                duration_seconds: duration,
                image_path: imagePath || null,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) { setConfirmDelete(true); return; }
        await onDelete();
    };

    return (
        <div className="border border-slate-200 overflow-hidden">
            {/* Row header */}
            <div
                className="flex items-center gap-3 p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 select-none"
                onClick={onToggle}
            >
                <span className="text-slate-400 text-sm w-5 text-center select-none">{index + 1}</span>

                {/* Thumbnail */}
                {slide.image_path ? (
                    <img
                        src={slide.image_path}
                        alt=""
                        className="w-16 h-10 object-cover shrink-0"
                    />
                ) : (
                    <div className="w-16 h-10 bg-slate-200 shrink-0 flex items-center justify-center">
                        <span className="text-slate-400 text-xs">no img</span>
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <p className="text-slate-800 text-sm truncate">
                        {slide.text_en ? slide.text_en.replace(/<[^>]+>/g, '').slice(0, 60) : '(no text)'}
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">
                        {slide.text_position} · {slide.duration_seconds}s
                    </p>
                </div>

                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={onMoveUp}
                        disabled={index === 0}
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer"
                    ><ChevronUp className="w-4 h-4" /></button>
                    <button
                        onClick={onMoveDown}
                        disabled={index === total - 1}
                        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer"
                    ><ChevronDown className="w-4 h-4" /></button>
                </div>
            </div>

            {/* Expanded editor */}
            {isExpanded && (
                <div className="p-4 bg-white border-t border-slate-200 space-y-4">
                    {/* Image upload */}
                    <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                            {t('uploadImage')}
                        </label>
                        <div className="flex items-center gap-3">
                            {imagePath && (
                                <img src={imagePath} alt="" className="w-24 h-16 object-cover" />
                            )}
                            <label className="cursor-pointer px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm border border-slate-300 transition-colors">
                                {uploading ? 'Uploading…' : t('uploadImage')}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                />
                            </label>
                        </div>
                    </div>

                    {/* Language tabs */}
                    <div>
                        <div className="flex gap-1 mb-2">
                            <button
                                onClick={() => setActiveTab('en')}
                                className={`px-3 py-1 text-xs font-medium cursor-pointer transition-colors ${activeTab === 'en' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >EN</button>
                            <button
                                onClick={() => setActiveTab('pt')}
                                className={`px-3 py-1 text-xs font-medium cursor-pointer transition-colors ${activeTab === 'pt' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >PT</button>
                        </div>
                        {activeTab === 'en' ? (
                            <RichEditor
                                key={`en-${slide.id}`}
                                label={t('textEn')}
                                initialContent={textEn}
                                onBlur={setTextEn}
                            />
                        ) : (
                            <RichEditor
                                key={`pt-${slide.id}`}
                                label={t('textPt')}
                                initialContent={textPt}
                                onBlur={setTextPt}
                            />
                        )}
                    </div>

                    {/* Position + Duration */}
                    <div className="flex items-center gap-6">
                        <div>
                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                                {t('position')}
                            </label>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setPosition('left')}
                                    className={`px-3 py-1 text-xs cursor-pointer transition-colors ${position === 'left' ? 'bg-slate-900 text-white font-medium' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                >{t('positionLeft')}</button>
                                <button
                                    onClick={() => setPosition('right')}
                                    className={`px-3 py-1 text-xs cursor-pointer transition-colors ${position === 'right' ? 'bg-slate-900 text-white font-medium' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                >{t('positionRight')}</button>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                                {t('duration')}
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                className="w-20 px-2 py-1 bg-white text-slate-800 text-sm border border-slate-300 outline-none focus:border-slate-500"
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <button
                            onClick={handleDelete}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer transition-colors ${confirmDelete ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                        >
                            <Trash2 className="w-4 h-4" />
                            {confirmDelete ? t('confirmDelete') : t('deleteSlide')}
                        </button>
                        {confirmDelete && (
                            <button
                                onClick={() => setConfirmDelete(false)}
                                className="text-slate-400 hover:text-slate-700 text-sm cursor-pointer"
                            >{t('cancel')}</button>
                        )}
                        {!imagePath && (
                            <span className="text-xs text-red-500">{t('uploadImage')} required</span>
                        )}
                        <div className="flex items-center gap-3">
                            <AnimatePresence>
                                {saved && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        className="px-3 py-1.5 bg-emerald-50 text-emerald-600 text-xs font-medium border border-emerald-100 flex items-center gap-1.5"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Saved
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <button
                                onClick={handleSave}
                                disabled={saving || !imagePath}
                                className="flex items-center gap-1.5 px-4 py-1.5 bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {saving ? (
                                    <div className="w-4 h-4 border-2 border-slate-300 border-t-white rounded-full animate-spin" />
                                ) : t('save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main Editor ─────────────────────────────────────────────────────────────

interface HeroSlideEditorProps {
    slides: HeroSlide[];
    onChange: (slides: HeroSlide[]) => void;
    onClose: () => void;
}

export default function HeroSlideEditor({ slides, onChange, onClose }: HeroSlideEditorProps) {
    const t = useTranslations('landing.heroSlideshow');
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [adding, setAdding] = useState(false);

    const handleAdd = async () => {
        setAdding(true);
        try {
            const newSlide = await api.createSlide({ order: slides.length });
            onChange([...slides, newSlide]);
            setExpandedId(newSlide.id);
        } finally {
            setAdding(false);
        }
    };

    const handleSave = async (slide: HeroSlide, updates: Partial<HeroSlide>) => {
        const updated = await api.updateSlide(slide.id, updates);
        onChange(slides.map(s => s.id === updated.id ? updated : s));
    };

    const handleDelete = async (slide: HeroSlide) => {
        await api.deleteSlide(slide.id);
        const remaining = slides.filter(s => s.id !== slide.id);
        onChange(remaining);
        if (expandedId === slide.id) setExpandedId(null);
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        const newSlides = [...slides];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newSlides[index], newSlides[swapIndex]] = [newSlides[swapIndex], newSlides[index]];

        // Reassign order values
        const reordered = newSlides.map((s, i) => ({ ...s, order: i }));
        onChange(reordered);

        // Persist reorder
        await api.reorderSlides(reordered.map(s => ({ id: s.id, order: s.order })));
    };

    return (
        <section className="bg-white px-6 md:px-10 pt-24 pb-8 border-t border-slate-200">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-slate-900 text-xl font-medium">{t('editSlides')}</h2>
                    <button
                        onClick={onClose}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:text-slate-900 text-sm cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                        {t('doneEditing')}
                    </button>
                </div>

                {slides.length === 0 && (
                    <p className="text-slate-400 italic text-sm mb-4">{t('noSlides')}</p>
                )}

                <div className="space-y-2 mb-4">
                    {slides.map((slide, index) => (
                        <SlideRow
                            key={slide.id}
                            slide={slide}
                            index={index}
                            total={slides.length}
                            isExpanded={expandedId === slide.id}
                            onToggle={() => setExpandedId(expandedId === slide.id ? null : slide.id)}
                            onMoveUp={() => handleMove(index, 'up')}
                            onMoveDown={() => handleMove(index, 'down')}
                            onSave={(updates) => handleSave(slide, updates)}
                            onDelete={() => handleDelete(slide)}
                        />
                    ))}
                </div>

                <button
                    onClick={handleAdd}
                    disabled={adding}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm border border-slate-300 cursor-pointer disabled:opacity-50 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    {adding ? 'Adding…' : t('addSlide')}
                </button>
            </div>
        </section>
    );
}
